import { Box, Flex, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import CDS from "design-system";
import useCaminoStore from "hooks/useCaminoStore";
import { useNavbar } from "hooks/useNavbar";
import AppPage from "layouts/AppPage";
import { useEffect } from "react";
import apiClient from "services/ApiClient";
import { Heading22 } from "components/Heading";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { DashboardStats } from "./models";
import SetUpIntegrationBanner from "./SetUpIntegrationCard";
import { useAnalytics } from "services/analytics";
import MyTasksCard from "./MyTasksCard";
import CalendarCard from "./CalendarCard";
import DashboardCard from "./DashboardCard";
import OnboardingTimelineCard from "./OnboardingTimelineCard";
import StartingCard from "./StartingCard";

export default function PeopleTeamDashboardPage() {
  const user = useCaminoStore((state) => state.currentUser);
  const { setBackLocationPath, setPageTitle } = useNavbar();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    setBackLocationPath(null);
    setPageTitle(`Welcome${user?.firstName ? `, ${user.firstName}` : "!"}`);

    return () => {
      setBackLocationPath(null);
      setPageTitle(null);
    };
  }, [setBackLocationPath, setPageTitle, user?.firstName]);

  useEffect(() => {
    trackPageView("dashboard", { dashboard_type: "people_team" });
  }, [trackPageView]);

  const { data: statsData } = useQuery<DashboardStats>({
    queryKey: ["dashboardPeopleTeamStats"],
    queryFn: () => apiClient.get(API_ROUTES.dashboard.peopleTeamStats),
    staleTime: 1000 * 60 * 3,
  });

  return (
    <AppPage>
      {statsData && (
        <VStack align="stretch" spacing={6} pb={8}>
          {!statsData.connectedIntegrations.messageService && (
            <SetUpIntegrationBanner connectedIntegrations={statsData.connectedIntegrations} />
          )}
          <Box>
            <Heading22>{`Welcome${user?.firstName ? `, ${user.firstName}` : "!"} 🌱`}</Heading22>
            <CDS.Text14 color="gray.500" mt={1}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </CDS.Text14>
          </Box>
          <StartingCard />
          <OnboardingTimelineCard />

          <Flex gap={6} align="flex-start" sx={{ "@media (max-width: 1100px)": { flexDirection: "column" } }}>
            <DashboardCard flex={1} minW={0} w="100%">
              <MyTasksCard />
            </DashboardCard>
            <DashboardCard flex={1} minW={0} w="100%">
              <CalendarCard />
            </DashboardCard>
          </Flex>
        </VStack>
      )}
    </AppPage>
  );
}
